// src/portal-app/more/InventoryScreen.js
//
// Plan 4 Task 5 — Inventory screen, migrated to the P4 pattern (spec §5).
// See operations/TransactionsScreen.js:32-55 for the pattern (and its file
// header) this follows; ProductsScreen.js is the closest reviewed model
// (Plan 4 Task 4) and this header only calls out decisions specific to
// Inventory.
//
// Stock threshold — VERIFIED, not assumed. Spec §1 complaint #8 names this
// screen explicitly, and a Plan 1-3 review separately caught an alert on
// another screen that understated stock severity, so this was checked
// rather than carried over on faith. frontend/src/pages/portal/
// PortalInventoryPage.js's StockBadge (lines 27-33) is the only real
// definition of low/out-of-stock anywhere in the codebase — the backend's
// `Inventory` model (backend/app/models/models.py:739) and its
// `InventoryResponse` schema store `current_stock` / `minimum_stock_level`
// as raw fields only; nothing computes an `is_low_stock` (or equivalent)
// field, and no router/service derives one either. The web page's logic is
// therefore the sole authority, and this screen's pre-existing
// `stockLevel()` (kept, unchanged) already matched it byte-for-byte:
//   stock = parseFloat(current_stock) || 0; min = parseFloat(minimum_stock_level) || 0
//   stock <= 0             -> 'out'  (checked FIRST — most severe)
//   min > 0 && stock <= min -> 'low'
//   otherwise               -> 'good'
// Checking 'out' before 'low' is what stops a zero-stock ingredient (which
// also satisfies `stock <= min` whenever a minimum is set) from ever being
// misclassified as merely "low" — the exact trap the task brief warns about.
//
// Hero — leads with the low-stock figure ("the low-stock figure is the fact
// that matters on this screen; lead with it" — task brief), not the
// ingredient total. HeroStat's `value` is `alertCount = outCount + lowCount`,
// NOT lowCount alone: a tenant with several OUT-of-stock ingredients and zero
// merely-"low" ones must not read as "0 needs attention" — that would be
// exactly the understated-severity defect the brief calls out. The
// ingredient total moves to HeroStat's `caption`. Because a single alert
// number doesn't say how many are fully out vs merely low, a breakdown line
// underneath spells both out in WORDS ("N out of stock" / "N low stock"),
// each with its own colour — colour is never the sole carrier of meaning
// (spec §9). The breakdown only renders when alertCount > 0; it is a summary,
// never a control.
//
// Cost per unit dropped from the row — the brief's row spec is exactly
// name / quantity+unit / stock status. Cost is back-office info without a
// scan-time need, the same reasoning ProductsScreen used to drop cost from
// its own row (see ProductsScreen.js:42-52). It stays fully editable via the
// purchase transaction sheet below.
//
// Supplier composed into the row's `secondary` slot (CondensedRow itself is
// untouched — one secondary string, so this is composition, not a new slot)
// when set; omitted otherwise, same idiom ProductsScreen used for category +
// stock. Minimum stock level is not independently surfaced on the row — it's
// already the input to the stock-status pill's wording, and the create form
// is where it's set.
//
// Row action buttons unchanged: Purchase / Usage / Delete beneath each
// CondensedRow, in the same row + action-row wrapper TransactionsScreen and
// ProductsScreen both use — keeps the pre-migration `ingredient-card-*`,
// `purchase-btn-*`, `usage-btn-*`, `delete-btn-*` testids alive.
//
// FilterRow (Out/Low/Good stock status) collapses into FilterSheet as a
// single categorical group — the "filter-row pileup" the task brief names
// for this screen (spec §1 complaint #8): the pre-migration screen showed a
// 3-stat summary bar, a search field, then a FilterRow, all before a single
// ingredient. Search stays a full field (not a nav-bar icon) outside
// SurfaceStates, matching the ProductsScreen precedent.
//
// Fixed in passing (same defect ProductsScreen and TransactionsScreen fixed
// for their own destructive flows): the delete ConfirmSheet used to pass an
// `error` prop. ConfirmSheet (moved to shell/kit in Plan 4 Task 1) has never
// accepted one — its real error slot is `children`, rendered inside the
// sheet above the action row. Moved the delete error there.
//
// PullToRefresh (Task 2) wraps the count line + list, calling load(). This
// screen never had its own overflow-y:auto wrapper, so there is nothing to
// remove.
//
// Stale-response guard (seqRef): unchanged behaviour — incremented on every
// load(); load() is already an async function, so it already returns a
// Promise PullToRefresh can await.
//
// TransactionSheet and CreateIngredientSheet are UNCHANGED by this task —
// neither is a list surface, and every field, payload shape and testid they
// use is preserved exactly.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useRef,
} from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { inventoryService } from '../../services/api';
import { SurfaceStates } from '../insights/kit';
import {
    SearchInput,
    ConfirmSheet,
    CondensedRow,
    FilterButton,
    FilterSheet,
    HeroStat,
    PullToRefresh,
    ensureKitCss,
    IconPackage,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.inv-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 80px;}

/* ---- hero ---- */
.inv-hero{margin:0 16px 16px;padding:18px 20px 16px;}
.inv-hero-breakdown{display:flex;align-items:center;gap:14px;flex-wrap:wrap;
  margin-block-start:10px;}
.inv-hero-out{font-size:12px;font-weight:700;color:var(--c-red);}
.inv-hero-low{font-size:12px;font-weight:700;color:var(--c-amber);}

/* ---- control row — FilterButton only ---- */
.inv-control-row{display:flex;justify-content:flex-end;padding:0 16px;
  margin-bottom:10px;}

/* ---- active-filter summary ---- */
.inv-filtersum{display:flex;align-items:center;gap:12px;
  margin:4px 16px 12px;}
.inv-filtersum-text{flex:1 1 auto;min-width:0;font-size:12px;font-weight:600;
  color:var(--c-textLight);overflow:hidden;text-overflow:ellipsis;
  white-space:nowrap;}
.inv-filtersum-clear{flex:none;min-height:44px;padding-inline:6px;border:none;
  background:transparent;font-family:inherit;font-size:12px;font-weight:700;
  color:var(--c-green);cursor:pointer;}

/* ---- search ---- */
.inv-search-wrap{padding:0 16px;margin-bottom:10px;}

/* ---- count line ---- */
.inv-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 16px 10px;}

/* ---- ingredient list ---- */
.inv-list{display:flex;flex-direction:column;gap:10px;padding:0 16px;}

/* ---- row wrap — CondensedRow plus the purchase/usage/delete actions ---- */
.inv-row-wrap{display:flex;flex-direction:column;gap:6px;}
.inv-action-row{margin-inline-start:44px;display:flex;gap:8px;flex-wrap:wrap;}
.inv-action-btn{min-height:44px;padding:0 14px;border-radius:12px;
  font-family:inherit;font-size:13px;font-weight:700;cursor:pointer;
  transition:transform .12s ease,background .12s ease;border:none;}
.inv-action-btn:active{transform:scale(.96);}

.inv-purchase-btn{
  background:color-mix(in srgb,var(--c-green) 10%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-green) 35%,transparent);
  color:var(--c-green);}
.inv-purchase-btn:hover{background:color-mix(in srgb,var(--c-green) 16%,transparent);}

.inv-usage-btn{
  background:color-mix(in srgb,var(--c-blue) 10%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-blue) 35%,transparent);
  color:var(--c-blue);}
.inv-usage-btn:hover{background:color-mix(in srgb,var(--c-blue) 16%,transparent);}

.inv-delete-btn{
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-red) 30%,transparent);
  color:var(--c-red);margin-inline-start:auto;}
.inv-delete-btn:hover{background:color-mix(in srgb,var(--c-red) 14%,transparent);}

/* ---- filtered-empty inline message (status stays 'ready') ---- */
.inv-filtered-empty{text-align:center;padding:40px 16px;
  color:var(--c-textLight);font-size:14px;font-weight:500;}

/* ---- FAB ---- */
.inv-fab{position:fixed;inset-inline-end:20px;bottom:calc(80px + var(--safe-bottom,0px));
  width:56px;height:56px;border-radius:50%;border:none;
  background:var(--c-green);color:var(--c-white);cursor:pointer;
  box-shadow:0 4px 16px color-mix(in srgb,var(--c-green) 40%,transparent);
  display:inline-flex;align-items:center;justify-content:center;
  transition:transform .15s ease,box-shadow .15s ease;z-index:100;}
.inv-fab:active{transform:scale(.92);}
.inv-fab:hover{box-shadow:0 6px 20px color-mix(in srgb,var(--c-green) 50%,transparent);}

/* ---- transaction sheet ---- */
.inv-tx-backdrop{position:fixed;inset:0;z-index:200;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.inv-tx-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:24px 20px calc(24px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  background:var(--c-surface);
  -webkit-backdrop-filter:blur(20px);backdrop-filter:blur(20px);}
.inv-tx-title{font-size:18px;font-weight:800;color:var(--c-ink);
  margin-bottom:4px;letter-spacing:-.3px;}
.inv-tx-subtitle{font-size:13px;font-weight:500;color:var(--c-textLight);
  margin-bottom:20px;}

/* ---- form fields (shared by tx + create) ---- */
.inv-form-group{margin-bottom:14px;}
.inv-form-label{display:block;font-size:11px;font-weight:700;
  text-transform:uppercase;letter-spacing:.7px;color:var(--c-textLight);
  margin-bottom:6px;}
.inv-form-required{color:var(--c-red);margin-inline-start:2px;}
.inv-form-input,.inv-form-select{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  outline:none;-webkit-appearance:none;appearance:none;
  font-variant-numeric:tabular-nums;}
.inv-form-input:focus,.inv-form-select:focus{border-color:var(--c-green);}
.inv-form-select{cursor:pointer;}
.inv-form-row{display:flex;gap:10px;}
.inv-form-row .inv-form-group{flex:1;}
.inv-form-actions{display:flex;gap:10px;margin-top:20px;}
.inv-form-cancel{flex:1;min-height:44px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 14%,transparent);
  border-radius:16px;
  background:color-mix(in srgb,var(--c-surface) 50%,transparent);
  font-family:inherit;font-size:15px;font-weight:700;color:var(--c-ink);
  cursor:pointer;transition:transform .15s ease;}
.inv-form-cancel:active{transform:scale(.97);}
.inv-form-save{flex:1;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  transition:transform .15s ease,opacity .15s ease;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;}
.inv-form-save:active{transform:scale(.97);}
.inv-form-save:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.inv-form-error{margin-top:10px;font-size:13px;font-weight:600;
  color:var(--c-red);}
.inv-form-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);animation:invSpin .75s linear infinite;}
@keyframes invSpin{to{transform:rotate(360deg);}}

/* ---- delete-confirm sheet error — rendered INSIDE the sheet as
   ConfirmSheet children (see file header: error isn't a real prop on this
   component; a sibling would sit behind its fixed backdrop). ---- */
.inv-sheet-error{font-size:12px;font-weight:600;color:var(--c-red);
  margin-top:4px;padding:0 4px;}

/* ---- create form sheet ---- */
.inv-create-backdrop{position:fixed;inset:0;z-index:200;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;overflow-y:auto;}
.inv-create-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:24px 20px calc(24px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  background:var(--c-surface);
  -webkit-backdrop-filter:blur(20px);backdrop-filter:blur(20px);}
.inv-create-title{font-size:18px;font-weight:800;color:var(--c-ink);
  margin-bottom:20px;letter-spacing:-.3px;}

@media (prefers-reduced-motion:reduce){
  .inv-fab,.inv-fab:active,
  .inv-action-btn,.inv-action-btn:active,
  .inv-form-cancel,.inv-form-cancel:active,
  .inv-form-save,.inv-form-save:active{transition:none;transform:none;}
  .inv-form-spinner{animation-duration:1.5s;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'inv-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Stock level helper — see file header for the verification of this   */
/* logic against frontend/src/pages/portal/PortalInventoryPage.js.     */
/* Unchanged from the pre-migration screen.                            */
/* ------------------------------------------------------------------ */
function stockLevel(item) {
    const stock = parseFloat(item.current_stock) || 0;
    const min   = parseFloat(item.minimum_stock_level) || 0;
    if (stock <= 0) return 'out';
    if (min > 0 && stock <= min) return 'low';
    return 'good';
}

/* ------------------------------------------------------------------ */
/* Stock label — words, not just colour (spec §9). Reuses the same     */
/* stock_out / stock_low / stock_good keys ProductsScreen's stockLabel */
/* and the pre-migration screen both already used.                     */
/* ------------------------------------------------------------------ */
function stockLabel(item, t) {
    const level = stockLevel(item);
    if (level === 'out') return t('stock_out', {}, 'Out of stock');
    if (level === 'low') return t('stock_low', {}, 'Low stock');
    return t('stock_good', {}, 'Good');
}

const STOCK_TONE = { out: 'red', low: 'amber', good: 'green' };

/* ------------------------------------------------------------------ */
/* Transaction bottom sheet — UNCHANGED by this task                   */
/* ------------------------------------------------------------------ */
function TransactionSheet({ item, txType, onClose, onSaved, currency, t }) {
    const [qty, setQty]         = useState('');
    const [cost, setCost]       = useState('');
    const [total, setTotal]     = useState('');
    const [totalTouched, setTotalTouched] = useState(false);
    const [notes, setNotes]     = useState('');
    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState(null);

    // Auto-calc total from qty × cost unless user touched total
    useEffect(() => {
        if (totalTouched) return;
        const q = parseFloat(qty) || 0;
        const c = parseFloat(cost) || 0;
        if (q > 0 && c > 0) {
            setTotal(String(Math.round(q * c * 100) / 100));
        } else {
            setTotal('');
        }
    }, [qty, cost, totalTouched]);

    const handleTotalChange = (v) => {
        setTotal(v);
        setTotalTouched(true);
    };

    const handleSave = async () => {
        if (!qty || parseFloat(qty) <= 0) return;
        setSaving(true);
        setError(null);
        try {
            const payload = {
                inventory_id:     item.inventory_id,
                transaction_type: txType,
                quantity:         parseFloat(qty),
            };
            if (cost)  payload.cost_per_unit = parseFloat(cost);
            if (total) payload.total_cost    = parseFloat(total);
            if (notes) payload.notes         = notes;

            await inventoryService.createInventoryTransaction(payload);
            onSaved();
        } catch (err) {
            setError(t('transaction_save_error', {}, 'Failed to record transaction. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const isPurchase = txType === 'purchase';

    return (
        <div className="inv-tx-backdrop">
            <div
                className="inv-tx-sheet"
                data-testid="transaction-sheet"
                data-type={txType}
            >
                <div className="inv-tx-title">
                    {isPurchase
                        ? t('record_purchase', {}, 'Record Purchase')
                        : t('record_usage', {}, 'Record Usage')}
                </div>
                <div className="inv-tx-subtitle">
                    {item.name}
                </div>

                {/* Quantity */}
                <div className="inv-form-group">
                    <label className="inv-form-label">
                        {t('quantity_label', {}, 'Quantity')}
                        <span className="inv-form-required" aria-hidden="true">*</span>
                    </label>
                    <input
                        data-testid="tx-quantity-input"
                        className="inv-form-input"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="any"
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                        placeholder="0"
                    />
                </div>

                {/* Cost per unit — only for purchases */}
                {isPurchase && (
                    <div className="inv-form-group">
                        <label className="inv-form-label">
                            {t('cost_per_unit_label', {}, 'Cost per unit')} ({currency})
                        </label>
                        <input
                            data-testid="tx-cost-input"
                            className="inv-form-input"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="any"
                            value={cost}
                            onChange={(e) => setCost(e.target.value)}
                            placeholder="0.00"
                        />
                    </div>
                )}

                {/* Total cost — auto-calc, editable override */}
                {isPurchase && (
                    <div className="inv-form-group">
                        <label className="inv-form-label">
                            {t('total_cost_label', {}, 'Total cost')} ({currency})
                        </label>
                        <input
                            data-testid="tx-total-input"
                            className="inv-form-input"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="any"
                            value={total}
                            onChange={(e) => handleTotalChange(e.target.value)}
                            placeholder="0.00"
                        />
                    </div>
                )}

                {/* Notes */}
                <div className="inv-form-group">
                    <label className="inv-form-label">
                        {t('transaction_notes', {}, 'Notes')}
                    </label>
                    <input
                        data-testid="tx-notes-input"
                        className="inv-form-input"
                        type="text"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t('transaction_notes_placeholder', {}, 'Optional notes…')}
                    />
                </div>

                {error && <div className="inv-form-error" role="alert">{error}</div>}

                <div className="inv-form-actions">
                    <button
                        data-testid="tx-cancel-btn"
                        className="inv-form-cancel"
                        onClick={onClose}
                        disabled={saving}
                    >
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        data-testid="tx-save-btn"
                        className="inv-form-save"
                        onClick={handleSave}
                        disabled={saving || !qty || parseFloat(qty) <= 0}
                    >
                        {saving && <span className="inv-form-spinner" aria-hidden="true" />}
                        {t('save', {}, 'Save')}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Create ingredient bottom sheet — UNCHANGED by this task              */
/* ------------------------------------------------------------------ */
const UNITS = ['kg', 'g', 'liters', 'ml', 'pieces', 'units'];

function CreateIngredientSheet({ onClose, onSaved, currency, t }) {
    const [name, setName]         = useState('');
    const [unit, setUnit]         = useState('kg');
    const [costPU, setCostPU]     = useState('');
    const [stock, setStock]       = useState('');
    const [minStock, setMinStock] = useState('');
    const [supplier, setSupplier] = useState('');
    const [saving, setSaving]     = useState(false);
    const [error, setError]       = useState(null);

    const handleSave = async () => {
        if (!name.trim() || !unit) return;
        setSaving(true);
        setError(null);
        try {
            const payload = {
                name:                name.trim(),
                unit_of_measurement: unit,
            };
            if (costPU)   payload.cost_per_unit         = parseFloat(costPU);
            if (stock)    payload.current_stock          = parseFloat(stock);
            if (minStock) payload.minimum_stock_level    = parseFloat(minStock);
            if (supplier) payload.supplier               = supplier.trim();

            await inventoryService.createInventoryItem(payload);
            onSaved();
        } catch (err) {
            setError(t('ingredient_create_error', {}, 'Failed to create ingredient. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="inv-create-backdrop">
            <div className="inv-create-sheet" data-testid="create-ingredient-form">
                <div className="inv-create-title">
                    {t('add_ingredient', {}, 'Add Ingredient')}
                </div>

                {/* Name */}
                <div className="inv-form-group">
                    <label className="inv-form-label">
                        {t('ingredient_name', {}, 'Name')}
                        <span className="inv-form-required" aria-hidden="true">*</span>
                    </label>
                    <input
                        data-testid="create-name-input"
                        className="inv-form-input"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('ingredient_name_placeholder', {}, 'e.g. Coffee Beans')}
                    />
                </div>

                {/* Unit of measurement */}
                <div className="inv-form-group">
                    <label className="inv-form-label">
                        {t('unit_of_measurement', {}, 'Unit of Measurement')}
                        <span className="inv-form-required" aria-hidden="true">*</span>
                    </label>
                    <select
                        data-testid="create-unit-select"
                        className="inv-form-select"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                    >
                        {UNITS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                        ))}
                    </select>
                </div>

                {/* Cost per unit + Current stock row */}
                <div className="inv-form-row">
                    <div className="inv-form-group">
                        <label className="inv-form-label">
                            {t('cost_per_unit_label', {}, 'Cost / unit')} ({currency})
                        </label>
                        <input
                            data-testid="create-cost-input"
                            className="inv-form-input"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="any"
                            value={costPU}
                            onChange={(e) => setCostPU(e.target.value)}
                            placeholder="0.00"
                        />
                    </div>
                    <div className="inv-form-group">
                        <label className="inv-form-label">
                            {t('current_stock', {}, 'Current stock')}
                        </label>
                        <input
                            data-testid="create-stock-input"
                            className="inv-form-input"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="any"
                            value={stock}
                            onChange={(e) => setStock(e.target.value)}
                            placeholder="0"
                        />
                    </div>
                </div>

                {/* Min stock level + Supplier row */}
                <div className="inv-form-row">
                    <div className="inv-form-group">
                        <label className="inv-form-label">
                            {t('minimum_stock', {}, 'Min stock')}
                        </label>
                        <input
                            data-testid="create-min-stock-input"
                            className="inv-form-input"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="any"
                            value={minStock}
                            onChange={(e) => setMinStock(e.target.value)}
                            placeholder="0"
                        />
                    </div>
                    <div className="inv-form-group">
                        <label className="inv-form-label">
                            {t('supplier_name', {}, 'Supplier')}
                        </label>
                        <input
                            data-testid="create-supplier-input"
                            className="inv-form-input"
                            type="text"
                            value={supplier}
                            onChange={(e) => setSupplier(e.target.value)}
                            placeholder={t('supplier_placeholder', {}, 'Optional')}
                        />
                    </div>
                </div>

                {error && <div className="inv-form-error" role="alert">{error}</div>}

                <div className="inv-form-actions">
                    <button
                        data-testid="create-cancel-btn"
                        className="inv-form-cancel"
                        onClick={onClose}
                        disabled={saving}
                    >
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        data-testid="create-save-btn"
                        className="inv-form-save"
                        onClick={handleSave}
                        disabled={saving || !name.trim() || !unit}
                    >
                        {saving && <span className="inv-form-spinner" aria-hidden="true" />}
                        {t('save', {}, 'Save')}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Ingredient row — CondensedRow plus the purchase/usage/delete action */
/* row beneath it, mirroring ProductsScreen's ProductRow (CondensedRow */
/* + status-dependent action(s), in a wrapper that keeps the           */
/* pre-migration `ingredient-card-*` testid alive).                    */
/* ------------------------------------------------------------------ */
function IngredientRow({ item, onPurchase, onUsage, onDelete, t }) {
    const level = stockLevel(item);
    const tone = STOCK_TONE[level];
    const qty = parseFloat(item.current_stock) || 0;
    const unit = item.unit_of_measurement || '';

    return (
        <div className="inv-row-wrap" data-testid={`ingredient-card-${item.inventory_id}`}>
            <CondensedRow
                icon={<IconPackage />}
                tone={tone}
                name={item.name}
                value={`${qty} ${unit}`.trim()}
                secondary={item.supplier || undefined}
                pill={{ label: stockLabel(item, t), tone }}
                testId={item.inventory_id}
            />
            <div className="inv-action-row">
                <button
                    type="button"
                    data-testid={`purchase-btn-${item.inventory_id}`}
                    className="inv-action-btn inv-purchase-btn"
                    onClick={() => onPurchase(item)}
                    aria-label={`${t('record_purchase', {}, 'Purchase')} ${item.name}`}
                >
                    {t('record_purchase', {}, 'Purchase')}
                </button>
                <button
                    type="button"
                    data-testid={`usage-btn-${item.inventory_id}`}
                    className="inv-action-btn inv-usage-btn"
                    onClick={() => onUsage(item)}
                    aria-label={`${t('record_usage', {}, 'Usage')} ${item.name}`}
                >
                    {t('record_usage', {}, 'Usage')}
                </button>
                <button
                    type="button"
                    data-testid={`delete-btn-${item.inventory_id}`}
                    className="inv-action-btn inv-delete-btn"
                    onClick={() => onDelete(item)}
                    aria-label={`${t('delete_ingredient', {}, 'Delete')} ${item.name}`}
                >
                    {/* Trash icon */}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.2"
                        strokeLinecap="round" strokeLinejoin="round"
                        aria-hidden="true">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14H6L5 6" />
                        <path d="M10 11v6M14 11v6" />
                        <path d="M9 6V4h6v2" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Main screen                                                          */
/* ------------------------------------------------------------------ */
export default function InventoryScreen() {
    ensureCss();
    ensureKitCss();

    const { t }        = useLanguage();
    const { getCurrency } = useAuth();
    const currency     = getCurrency();

    const [items, setItems]         = useState([]);
    const [status, setStatus]       = useState('loading'); // 'loading' | 'error' | 'empty' | 'ready'
    const [search, setSearch]       = useState('');
    // stockFilter: null (all) | 'out' | 'low' | 'good' — collapsed into
    // FilterSheet (see file header). Default null preserves the
    // pre-migration screen's "show everything" first paint.
    const [stockFilter, setStockFilter] = useState(null);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);

    // Transaction sheet state
    const [txItem, setTxItem]       = useState(null);
    const [txType, setTxType]       = useState(null); // 'purchase' | 'usage'

    // Delete confirm state
    const [deleteItem, setDeleteItem]   = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Create sheet state
    const [showCreate, setShowCreate] = useState(false);

    // Stale-response guard
    const seqRef = useRef(0);

    const load = useCallback(async () => {
        const seq = ++seqRef.current;
        setStatus('loading');
        try {
            const data = await inventoryService.getAllInventory();
            if (seq !== seqRef.current) return;
            const list = data || [];
            setItems(list);
            setStatus(list.length === 0 ? 'empty' : 'ready');
        } catch {
            if (seq !== seqRef.current) return;
            setStatus('error');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Derived: hero (whole inventory, unaffected by filters) ────────
    const total = items.length;
    const outCount = items.filter((i) => stockLevel(i) === 'out').length;
    const lowCount = items.filter((i) => stockLevel(i) === 'low').length;
    const alertCount = outCount + lowCount;

    // ── Derived: filtered list ──────────────────────────────────────
    const filtered = items.filter((item) => {
        const nameMatch = !search || (item.name || '').toLowerCase().includes(search.toLowerCase());
        const filterMatch = stockFilter === null || stockLevel(item) === stockFilter;
        return nameMatch && filterMatch;
    });

    // ── Derived: FilterSheet groups + active-filter summary ───────────
    const stockOptions = [
        { id: 'out', label: t('stock_out', {}, 'Out of stock') },
        { id: 'low', label: t('stock_low', {}, 'Low stock') },
        { id: 'good', label: t('stock_good', {}, 'Good') },
    ];
    const filterGroups = [
        { id: 'stock', label: t('stock_status', {}, 'Stock Status'), options: stockOptions },
    ];
    const filterValue = { stock: stockFilter };
    const activeFilterCount = stockFilter ? 1 : 0;
    const activeFilterText = stockOptions.find((o) => o.id === stockFilter)?.label || '';

    const handleFilterChange = (groupId, optionId) => {
        if (groupId === 'stock') setStockFilter(optionId);
    };

    const clearAllFilters = () => {
        setStockFilter(null);
        setSearch('');
    };

    // ── Handlers ──────────────────────────────────────────────────────
    const handleTxSaved = useCallback(() => {
        setTxItem(null);
        setTxType(null);
        load();
    }, [load]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteItem) return;
        setDeleteLoading(true);
        setDeleteError(null);
        try {
            await inventoryService.deleteInventoryItem(deleteItem.inventory_id);
            setDeleteItem(null);
            load();
        } catch {
            setDeleteError(t('ingredient_delete_error', {}, 'Failed to delete ingredient. Please try again.'));
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteItem, load, t]);

    const handleCreateSaved = useCallback(() => {
        setShowCreate(false);
        load();
    }, [load]);

    const ptrDisabled = filterSheetOpen || showCreate || !!deleteItem || !!(txItem && txType);

    // ── Render ────────────────────────────────────────────────────────
    return (
        <div className="inv-wrap">
            {/* Hero card — whole-inventory summary, OUTSIDE SurfaceStates so it
                never vanishes. Unaffected by the stock filter or search — see
                file header for why it leads with alertCount, not lowCount. */}
            <GlassCard veil className="inv-hero" data-testid="inventory-hero">
                <HeroStat
                    label={t('needs_restocking', {}, 'Needs Restocking')}
                    value={alertCount}
                    caption={t('n_ingredients_tracked', { count: total }, '{count} ingredients tracked')}
                />
                {alertCount > 0 && (
                    <div className="inv-hero-breakdown" data-testid="hero-breakdown">
                        {outCount > 0 && (
                            <span className="inv-hero-out" data-testid="hero-out-count">
                                {t('out_of_stock_summary', { count: outCount }, '{count} out of stock')}
                            </span>
                        )}
                        {lowCount > 0 && (
                            <span className="inv-hero-low" data-testid="hero-low-count">
                                {t('low_stock_summary', { count: lowCount }, '{count} low stock')}
                            </span>
                        )}
                    </div>
                )}
            </GlassCard>

            {/* Control row — FilterButton only; no RangeBar equivalent here. */}
            <div className="inv-control-row">
                <FilterButton
                    activeCount={activeFilterCount}
                    onPress={() => setFilterSheetOpen(true)}
                />
            </div>

            {/* Active-filter summary — OUTSIDE SurfaceStates so a filtered-to-
                empty result still shows the means to clear the filter. */}
            {activeFilterCount > 0 && (
                <div className="inv-filtersum">
                    <span className="inv-filtersum-text">{activeFilterText}</span>
                    <button
                        type="button"
                        className="inv-filtersum-clear"
                        data-testid="clear-filters"
                        onClick={clearAllFilters}
                    >
                        {t('clear', {}, 'Clear')}
                    </button>
                </div>
            )}

            {/* Search — OUTSIDE SurfaceStates, alongside the other controls, for
                the same reason as Transactions/Products: a term matching
                nothing must not unmount the field mid-keystroke. */}
            <div className="inv-search-wrap">
                <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder={t('search_ingredients', {}, 'Search ingredients…')}
                />
            </div>

            <PullToRefresh onRefresh={() => load()} disabled={ptrDisabled}>
                <SurfaceStates
                    status={status}
                    onRetry={load}
                    emptyTitle={t('no_ingredients', {}, 'No ingredients yet')}
                    emptyMsg={t('no_ingredients_msg', {}, 'Add your first ingredient using the + button.')}
                >
                    {/* Count line */}
                    <div className="inv-count-line" data-testid="ingredients-count-line">
                        {t('n_ingredients', { count: filtered.length }, '{count} Ingredients')}
                    </div>

                    {filtered.length > 0 ? (
                        <div className="inv-list">
                            {filtered.map((item) => (
                                <IngredientRow
                                    key={item.inventory_id}
                                    item={item}
                                    onPurchase={(i) => { setTxItem(i); setTxType('purchase'); }}
                                    onUsage={(i) => { setTxItem(i); setTxType('usage'); }}
                                    onDelete={(i) => { setDeleteItem(i); setDeleteError(null); }}
                                    t={t}
                                />
                            ))}
                        </div>
                    ) : (
                        // Filtering client-side never flips `status` away from
                        // 'ready' — so a search/filter producing zero matches
                        // renders this inline message inside the ready branch.
                        <div className="inv-filtered-empty">
                            {t('no_ingredients_filter_msg', {}, 'No ingredients match your filters.')}
                        </div>
                    )}
                </SurfaceStates>
            </PullToRefresh>

            {/* FAB — add ingredient */}
            <button
                type="button"
                data-testid="fab-add-ingredient"
                className="inv-fab"
                onClick={() => setShowCreate(true)}
                aria-label={t('add_ingredient', {}, 'Add ingredient')}
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round"
                    aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
            </button>

            {/* Transaction sheet */}
            {txItem && txType && (
                <TransactionSheet
                    item={txItem}
                    txType={txType}
                    onClose={() => { setTxItem(null); setTxType(null); }}
                    onSaved={handleTxSaved}
                    currency={currency}
                    t={t}
                />
            )}

            {/* Delete confirmation — error rendered INSIDE the sheet (children),
                see file header for why. */}
            <ConfirmSheet
                open={!!deleteItem}
                onClose={() => { setDeleteItem(null); setDeleteError(null); }}
                onConfirm={handleDeleteConfirm}
                title={t('delete_ingredient', {}, 'Delete Ingredient')}
                message={
                    deleteItem
                        ? t('delete_ingredient_confirm', {}, `Delete "${deleteItem.name}"? This cannot be undone.`)
                        : ''
                }
                confirmLabel={t('delete', {}, 'Delete')}
                destructive
                loading={deleteLoading}
            >
                {deleteError && (
                    <div className="inv-sheet-error" data-testid="delete-error">
                        {deleteError}
                    </div>
                )}
            </ConfirmSheet>

            {/* Create ingredient sheet */}
            {showCreate && (
                <CreateIngredientSheet
                    onClose={() => setShowCreate(false)}
                    onSaved={handleCreateSaved}
                    currency={currency}
                    t={t}
                />
            )}

            {/* Filter sheet — stock status */}
            <FilterSheet
                open={filterSheetOpen}
                onClose={() => setFilterSheetOpen(false)}
                groups={filterGroups}
                value={filterValue}
                onChange={handleFilterChange}
                onClear={clearAllFilters}
            />
        </div>
    );
}
